import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type {
  OfferPhase,
  OvertimeSplit,
} from "@/lib/offer-calc";

export type OfferStatus = "draft" | "sent" | "accepted" | "lost";
export type OfferPricingType = "hourly" | "fixed";
/**
 * VIB-1304: sannsynlighetskategori satt ved sending. Bak feature_offer_pipeline
 * — se src/lib/feature-flags.ts. UI-etiketter i OfferProbabilityBadge.
 */
export type OfferProbability = "low" | "likely" | "very_likely";

export interface OfferLineTemplate {
  id: string;
  name: string;
  phase: OfferPhase;
  category: string;
  cost_type: string;
  pricing_type: OfferPricingType;
  default_unit: string;
  default_rate: number | null;
  sort_order: number;
  is_active: boolean;
  /** VIB-909: medgått-produkt — føres fritt mot uten tildeling, materialiserer mersalg. */
  is_medgatt?: boolean;
  /** Hvis true, legges templaten automatisk til som linje på nye tilbud. */
  is_default_for_offers?: boolean;
  /** Default mengde når templaten auto-legges til (f.eks. 4t for Reisetid). */
  default_quantity?: number | null;
  /** Foreslått overtidssats når PL aktiverer overtid på en linje (VIB-663f). */
  default_overtime_rate?: number | null;
  /** Valgfri lengre beskrivelse — vises som tooltip-preview i katalog-søk (VIB-663c). */
  description?: string | null;
}

export interface Offer {
  id: string;
  project_id: string | null;
  created_by: string;
  customer_id: string | null;
  customer_name: string | null;
  title: string;
  description: string | null;
  status: OfferStatus;
  overhead_pct: number;
  discount_pct: number;
  discount_amount: number;
  startup_invoice_pct: number;
  final_invoice_pct: number;
  total_amount: number;
  /** Per-tilbud override for timesats — null betyr bruk system_config default (VIB-555) */
  hourly_rate: number | null;
  /** Vis Forbehold/faktureringsvilkår som fotnote i kopier-bilde og Keynote. Default true. */
  show_invoice_terms: boolean;
  /** Fleksible faktureringsterminer ([{label,pct}]). Sum av pct = 100. */
  invoice_terms: Array<{ label: string; pct: number }>;
  /**
   * VIB-664: fritt redigerbar tekst som vises mellom budsjettabell og
   * faktureringsvilkår i eksport (PNG + Keynote). Null/tom = ingen tekst.
   */
  custom_terms_text: string | null;
  /**
   * VIB-663a: Når true rutes auto-tilbudsskriving-linja til Konseptutvikling-malen
   * (preprod) i stedet for default Tilbudsskriving under Prosjektledelse.
   */
  concept_routing: boolean;
  /**
   * VIB-789: Tilbudsskrivere som skal få Tilbudsskriving-credit ved aksept.
   * Sum av share_pct = 100. Default [{ user_id: created_by, share_pct: 100 }].
   */
  writers: Array<{ user_id: string; share_pct: number }>;
  /** VIB-993: reise faktureres separat (medgått). Overføres til prosjektet ved
   *  konvertering og vises som faktureringsvilkår i eksport. Default false. */
  bills_travel_per_medgatt: boolean;
  /**
   * VIB-2086: reise i overhead-grunnlaget. Nye tilbud default true.
   * Eksisterende rader er false (ikke tilbakevirkende).
   */
  include_travel_in_overhead: boolean;
  /** VIB-1136: Hvor tilbudet kom fra. 'import' dropper auto-linjer. */
  source: "manual" | "import";
  accepted_at: string | null;
  sent_at: string | null;
  /**
   * VIB-1304: sannsynlighetskategori. NULL = ikke satt (ekskludert fra vektet
   * KPI, vises på boardet med "Sett sannsynlighet"-hint). Bak feature_offer_pipeline.
   */
  probability: OfferProbability | null;
  /**
   * VIB-1304: gyldighetsdato (ISO-dato, YYYY-MM-DD). Settes automatisk til
   * sent_at + 45 dager når status→sent (kun hvis tom), manuelt justerbar.
   * "Utløpt" er avledet (status='sent' AND valid_until < i dag), ingen egen
   * status. Bak feature_offer_pipeline.
   */
  valid_until: string | null;
  /**
   * VIB-2094: rådgivers estimat på når kunden bestemmer seg (ISO-dato).
   * Påkrevd i UI ved sending; kolonnen er NULL-able så tilbud som allerede sto
   * som `sent` da feltet kom ikke brytes.
   *
   * Uavhengig av `valid_until`, som er prisens gyldighet. Et tilbud kan godt ha
   * forventet close etter at gyldigheten går ut — det er nettopp et slikt
   * tilbud DL vil se på.
   */
  expected_close_date: string | null;
  /** VIB-1304: satt når status→lost, speiler accepted_at-mønsteret. */
  lost_at: string | null;
  /**
   * VIB-1304 (Erik 15.07): påkrevd i UI ved statusendring til lost via
   * LostReasonDialog. Analysegrunnlag for VIB-1312.
   */
  lost_reason: string | null;
  /** CRM-2.1: prosessen tilbudet hører til (offers.opportunity_id). */
  opportunity_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface OfferWithCustomer extends Offer {
  customer: { id: string; name: string } | null;
  creator: { id: string; full_name: string } | null;
}

export interface OfferLine {
  id: string;
  offer_id: string;
  template_id: string | null;
  phase: OfferPhase;
  description: string;
  cost_type: string;
  pricing_type: OfferPricingType;
  quantity: number;
  unit: string;
  unit_price: number;
  /** Innkjøpspris for eksterne/fastpris-linjer. 0 for interne (VIB-555) */
  purchase_price: number;
  amount: number;
  comment: string | null;
  sort_order: number;
  /** Auto-genererte linjer (f.eks. tilbudsskriving) skal ikke redigeres manuelt. */
  is_auto_generated?: boolean;
  /** Identifiserer typen auto-linje. Brukes ved konvertering for å filtrere. */
  auto_kind?: string | null;
  /** Overtidssplit (VIB-663f). NULL = ingen split, linja oppfører seg vanlig. */
  overtime_split?: OvertimeSplit | null;
  /** VIB-663e: hvilken seksjon linja tilhører. null = hovedbudsjett. */
  section_id?: string | null;
  /**
   * VIB-2097: tidsbasis (kr/t) linja sist sto på i t/d. Leses når enheten
   * byttes fra fast/stk tilbake til t/d. null = katalogens timesats.
   */
  rate_basis_hourly?: number | null;
  /** VIB-2980: Idé-kortet linjen ble laget fra. Gir «Fra idé» i budsjettet. */
  source_idea_card_id?: string | null;
}

export interface OfferTemplate {
  id: string;
  code: string;
  name: string;
  description: string | null;
  kind: "size" | "addon";
  sort_order: number;
  is_active: boolean;
}

export interface OfferTemplateLine {
  id: string;
  template_id: string;
  /** VIB-701: FK til katalogen. NOT NULL etter drop-migrasjon. */
  line_template_id: string;
  /** VIB-701: joined data fra offer_line_templates. */
  line_template: OfferLineTemplate;
  /** Per-mal-mengde (XS=4t, M=8t). */
  quantity: number;
  sort_order: number;
}

/**
 * E2E-testbrukeren oppretter tilbud i samme database som prod (det
 * finnes ikke isolert test-DB enda). Filtrer dem ut av tilbudslisten
 * slik at test-data ikke forurenser prod-UI.
 */
const TEST_USER_EMAIL = "test@solvrev.no";

// ── Queries ──────────────────────────────────────────────────

export function useOfferLineTemplates() {
  return useQuery({
    queryKey: ["offer-line-templates"],
    queryFn: async (): Promise<OfferLineTemplate[]> => {
      const { data, error } = await supabase
        .from("offer_line_templates")
        .select("*")
        .eq("is_active", true)
        .order("phase")
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as OfferLineTemplate[];
    },
    staleTime: 10 * 60 * 1000,
  });
}

export function useOfferTemplates() {
  return useQuery({
    queryKey: ["offer-templates"],
    queryFn: async (): Promise<OfferTemplate[]> => {
      const { data, error } = await supabase
        .from("offer_templates")
        .select("*")
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as OfferTemplate[];
    },
    staleTime: 10 * 60 * 1000,
  });
}

export function useOffers() {
  return useQuery({
    queryKey: ["offers"],
    queryFn: async (): Promise<OfferWithCustomer[]> => {
      const { data, error } = await supabase
        .from("offers")
        .select(
          `*,
           customer:customers(id, name),
           creator:users!offers_created_by_fkey(id, full_name, email)`,
        )
        .order("created_at", { ascending: false });
      if (error) throw error;
      const rows = (data ?? []) as unknown as Array<
        OfferWithCustomer & { creator: { email?: string } | null }
      >;
      return rows.filter((o) => o.creator?.email !== TEST_USER_EMAIL);
    },
  });
}

export function useOffer(offerId: string | undefined) {
  return useQuery({
    queryKey: ["offer", offerId],
    enabled: !!offerId,
    queryFn: async (): Promise<OfferWithCustomer> => {
      const { data, error } = await supabase
        .from("offers")
        .select(
          `*,
           customer:customers(id, name),
           creator:users!offers_created_by_fkey(id, full_name)`,
        )
        .eq("id", offerId!)
        .single();
      if (error) throw error;
      return data as unknown as OfferWithCustomer;
    },
  });
}

export function useOfferLines(offerId: string | undefined) {
  return useQuery({
    queryKey: ["offer-lines", offerId],
    enabled: !!offerId,
    queryFn: async (): Promise<OfferLine[]> => {
      const { data, error } = await supabase
        .from("offer_lines")
        .select("*")
        .eq("offer_id", offerId!)
        .order("phase")
        .order("sort_order")
        // Stabil tiebreaker: uten denne gir like sort_order (f.eks. auto-
        // tilbudsskriving-linja + første linje i fasen) ikke-deterministisk
        // rekkefølge, så linjene «hopper» ved hver refetch (på hver blur).
        // offer_lines har ingen created_at-kolonne, så bruk id (uuid) —
        // deterministisk og stopper jitteren.
        .order("id");
      if (error) throw error;
      return (data ?? []) as OfferLine[];
    },
  });
}
